import React, { useState, useRef } from 'react';
import { askJarvis } from '../../services/claudeAI';
import { saveKnowledge, saveKnowledgeChunk, saveRule } from '../../services/storage';
import { THEME } from '../../styles/theme';

const EXTRACT_PROMPT = `You are JARVIS Knowledge Extractor. Extract ALL trading knowledge from the text below.
Convert it into structured, actionable learnings specifically for XAU/USD trading.

Extract and organize into:

1. TRADING RULES — Clear, specific rules a trader must follow
   Format: "Rule: [condition] → [action]"

2. MARKET BEHAVIORS — How the market tends to behave in specific situations
   Format: "Behavior: [situation] → [what typically happens]"

3. KEY CONCEPTS — Important trading concepts explained simply
   Format: "Concept: [name] — [explanation]"

4. ENTRY CRITERIA — Specific conditions needed before entering a trade
5. RISK MANAGEMENT — Money management and risk rules
6. PSYCHOLOGICAL INSIGHTS — Mental/emotional patterns to be aware of
7. KEY PRICE LEVELS OR ZONES — Any mentioned price levels or zones

After extraction, give:
- SUMMARY: 3-5 most important takeaways
- APPLICABLE TO SIR: How this specifically applies to XAU/USD trading with Sir's ICT/SMC style

Be thorough. Extract EVERY useful piece of information. Ignore irrelevant content.`;

const S = {
  wrap:     { padding: '20px', maxWidth: '900px', margin: '0 auto' },
  title:    { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:      { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' },
  tabs:     { display: 'flex', gap: '8px', marginBottom: '20px' },
  tab:      (a) => ({ padding: '9px 20px', borderRadius: '8px', border: `1px solid ${a ? THEME.colors.blue : 'rgba(255,255,255,0.1)'}`, background: a ? 'rgba(0,212,255,0.1)' : 'transparent', color: a ? THEME.colors.blue : '#9ca3af', cursor: 'pointer', fontWeight: '700', fontSize: '13px' }),

  card:     { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  label:    { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '6px' },
  input:    { width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: THEME.fonts.body },
  textarea: { width: '100%', padding: '12px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', resize: 'vertical', minHeight: '200px', boxSizing: 'border-box', fontFamily: THEME.fonts.body, lineHeight: '1.6' },
  btn:      (col=THEME.colors.blue) => ({ padding: '11px 24px', borderRadius: '8px', border: 'none', background: col, color: col === THEME.colors.blue ? '#000' : '#fff', cursor: 'pointer', fontWeight: '700', fontSize: '14px', marginRight: '10px', marginTop: '12px' }),
  btnGhost: { padding: '9px 18px', borderRadius: '8px', border: `1px solid rgba(0,212,255,0.3)`, background: 'transparent', color: THEME.colors.blue, cursor: 'pointer', fontSize: '13px', fontWeight: '700', marginRight: '8px', marginTop: '12px' },

  dropzone: (drag) => ({ border: `2px dashed ${drag ? THEME.colors.blue : 'rgba(0,212,255,0.2)'}`, borderRadius: '10px', padding: '30px', textAlign: 'center', cursor: 'pointer', background: drag ? 'rgba(0,212,255,0.05)' : 'transparent', transition: 'all 0.2s' }),
  dropText: { fontSize: '15px', color: '#e2e8f0', marginBottom: '4px', fontWeight: '600' },
  dropSub:  { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },

  resultCard:{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '12px', padding: '20px', marginTop: '16px' },
  resultTitle:{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.blue, marginBottom: '12px', fontFamily: THEME.fonts.hud },
  resultText: { fontSize: '14px', color: '#e2e8f0', lineHeight: '1.8', whiteSpace: 'pre-wrap' },

  loading:  { color: THEME.colors.textMuted, fontSize: '14px', padding: '20px 0' },
  error:    { color: '#ef4444', fontSize: '13px', padding: '12px', background: '#ef444411', borderRadius: '8px', marginTop: '10px' },
  charCount:{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', textAlign: 'right', marginTop: '4px' },
  success:  { color: '#10b981', fontSize: '13px', fontWeight: '700', marginTop: '8px' },
};

export default function LearningImport() {
  const [tab,       setTab]       = useState('text');
  const [text,      setText]      = useState('');
  const [ytUrl,     setYtUrl]     = useState('');
  const [pdfName,   setPdfName]   = useState('');
  const [pdfText,   setPdfText]   = useState('');
  const [loading,   setLoading]   = useState(false);
  const [extracting,setExtracting]= useState(false);
  const [result,    setResult]    = useState('');
  const [error,     setError]     = useState('');
  const [saved,     setSaved]     = useState(false);
  const [drag,      setDrag]      = useState(false);
  const [title,     setTitle]     = useState('');

  // JSON import state
  const [jsonText,     setJsonText]     = useState('');
  const [jsonPreview,  setJsonPreview]  = useState(null);
  const [jsonError,    setJsonError]    = useState('');
  const [jsonSaved,    setJsonSaved]    = useState(false);
  const [jsonStats,    setJsonStats]    = useState(null);

  const fileRef = useRef();

  const getInputText = () => {
    if (tab === 'pdf') return pdfText;
    if (tab === 'youtube') return text;
    return text;
  };

  const loadPDF = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      setError('Please upload a PDF file');
      return;
    }
    setLoading(true);
    setError('');
    setPdfName(file.name);

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        const res = await fetch('/api/learn/pdf', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ base64 }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setPdfText(data.text);
        setTitle(file.name.replace('.pdf', ''));
        setLoading(false);
      };
      reader.readAsDataURL(file);
    } catch (e) {
      setError(e.message);
      setLoading(false);
    }
  };

  const extract = async () => {
    const inputText = getInputText();
    if (!inputText.trim()) { setError('Please add content first'); return; }
    if (inputText.length < 100) { setError('Content too short — need at least 100 characters'); return; }

    setExtracting(true);
    setResult('');
    setError('');
    setSaved(false);

    const prompt = `${EXTRACT_PROMPT}

SOURCE TYPE: ${tab === 'youtube' ? 'YouTube/Video Transcript' : tab === 'pdf' ? 'PDF Document' : 'Text/Notes'}
TITLE: ${title || 'Untitled'}

CONTENT TO EXTRACT FROM:
---
${inputText.slice(0, 8000)}
---`;

    try {
      const resp = await askJarvis([{ role: 'user', content: prompt }], '', 2000);
      setResult(resp);
    } catch (e) {
      setError(e.message);
    }
    setExtracting(false);
  };

  const saveToKnowledge = () => {
    if (!result) return;
    saveKnowledge({
      type:        tab === 'youtube' ? 'video' : tab === 'pdf' ? 'pdf' : 'notes',
      source:      tab === 'youtube' ? (ytUrl || 'YouTube') : tab === 'pdf' ? pdfName : 'Manual Import',
      title:       title || `Learning Import — ${new Date().toLocaleDateString('en-IN')}`,
      raw_content: result,
      confidence:  80,
    });
    setSaved(true);
  };

  const reset = () => {
    setText(''); setYtUrl(''); setPdfText(''); setPdfName('');
    setResult(''); setError(''); setSaved(false); setTitle('');
  };

  // ── JSON Import logic ─────────────────────────────────────────────────────
  const parseJSON = () => {
    setJsonError('');
    setJsonPreview(null);
    setJsonSaved(false);
    setJsonStats(null);
    if (!jsonText.trim()) { setJsonError('JSON paste kara'); return; }
    try {
      const parsed = JSON.parse(jsonText);
      // Accept both: { chunks: [...] } and direct array [...]
      const chunks = Array.isArray(parsed) ? parsed : (parsed.chunks || []);
      if (!chunks.length) { setJsonError('chunks array empty aahe'); return; }

      // Validate at least first chunk has title + content
      if (!chunks[0].title && !chunks[0].content) {
        setJsonError('Each chunk la "title" ani "content" field pahije');
        return;
      }

      const source = parsed.source || parsed.author || 'JSON Import';
      setJsonPreview({ source, date: parsed.date, chunks });
    } catch (e) {
      setJsonError('Invalid JSON: ' + e.message);
    }
  };

  const importJSON = () => {
    if (!jsonPreview) return;
    const { source, chunks } = jsonPreview;
    let chunkCount = 0;
    let ruleCount  = 0;

    chunks.forEach(chunk => {
      // Save every chunk to Knowledge Chunks (RAG searchable)
      saveKnowledgeChunk({
        source:   source,
        category: chunk.category || 'concept',
        title:    chunk.title    || '',
        content:  chunk.content  || '',
        tags:     chunk.tags     || [],
      });
      chunkCount++;

      // Also save "rule" and "checklist" category items as formal Rules
      if (chunk.category === 'rule') {
        saveRule({
          seed_id:   `json_rule_${chunk.title?.slice(0,30).replace(/\s+/g,'_')}`,
          title:     chunk.title,
          condition: chunk.content,
          action:    'Apply as per rule',
          source:    source,
          notes:     (chunk.tags || []).join(', '),
        });
        ruleCount++;
      }
    });

    // Also save as one knowledge entry (for Knowledge Base screen)
    saveKnowledge({
      type:        'notes',
      source:      source,
      title:       source,
      raw_content: chunks.map(c => `[${c.category?.toUpperCase()}] ${c.title}\n${c.content}`).join('\n\n'),
      confidence:  90,
    });

    setJsonStats({ chunkCount, ruleCount, source });
    setJsonSaved(true);
  };

  const inputText = getInputText();

  return (
    <div style={S.wrap}>
      <div style={S.title}>LEARNING IMPORT</div>
      <div style={S.sub}>
        Import trading knowledge from YouTube videos, PDFs, or any text — JARVIS extracts structured rules and saves to Knowledge Base.
      </div>

      <div style={S.tabs}>
        {[['text','📝 Text / Notes'],['youtube','▶️ YouTube'],['pdf','📄 PDF'],['json','🗂️ JSON Direct']].map(([id,label]) => (
          <button key={id} style={S.tab(tab===id)} onClick={() => { setTab(id); setResult(''); setError(''); setJsonError(''); setJsonPreview(null); setJsonSaved(false); }}>{label}</button>
        ))}
      </div>

      {/* TEXT tab */}
      {tab === 'text' && (
        <div style={S.card}>
          <div style={S.label}>TITLE (optional)</div>
          <input style={S.input} placeholder="ICT London Session Notes, Rayner Teo Lesson..." value={title} onChange={e => setTitle(e.target.value)}/>
          <div style={{ height: '12px' }}/>
          <div style={S.label}>PASTE YOUR NOTES / TRANSCRIPT / LEARNING</div>
          <textarea
            style={S.textarea}
            placeholder="Paste any trading content here:
• Notes from a trading course
• Transcript of a video you watched
• Something you learned today
• ICT concept explanation
• Any text with trading knowledge..."
            value={text}
            onChange={e => setText(e.target.value)}
          />
          <div style={S.charCount}>{text.length.toLocaleString()} characters</div>
        </div>
      )}

      {/* YOUTUBE tab */}
      {tab === 'youtube' && (
        <div style={S.card}>
          <div style={S.label}>YOUTUBE VIDEO URL (for reference only)</div>
          <input style={S.input} placeholder="https://youtube.com/watch?v=..." value={ytUrl} onChange={e => setYtUrl(e.target.value)}/>
          <div style={{ height: '12px' }}/>
          <div style={S.label}>VIDEO TITLE</div>
          <input style={S.input} placeholder="ICT Mentorship - Order Blocks, Rayner Teo..." value={title} onChange={e => setTitle(e.target.value)}/>
          <div style={{ height: '12px' }}/>
          <div style={S.label}>PASTE VIDEO TRANSCRIPT OR YOUR NOTES FROM IT</div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginBottom: '8px' }}>
            How to get transcript: YouTube → ⋯ → Show transcript → Copy all text
          </div>
          <textarea
            style={S.textarea}
            placeholder="Paste the video transcript here...
Or type your own notes from the video.

Example: 'In this video, ICT explains that Order Blocks are the last opposing candle before a significant move. For a bullish OB, look for the last bearish candle before price breaks structure to the upside...'"
            value={text}
            onChange={e => setText(e.target.value)}
          />
          <div style={S.charCount}>{text.length.toLocaleString()} characters</div>
        </div>
      )}

      {/* PDF tab */}
      {tab === 'pdf' && (
        <div style={S.card}>
          {!pdfText ? (
            <>
              <div
                style={S.dropzone(drag)}
                onDragOver={e => { e.preventDefault(); setDrag(true); }}
                onDragLeave={() => setDrag(false)}
                onDrop={e => { e.preventDefault(); setDrag(false); loadPDF(e.dataTransfer.files[0]); }}
                onClick={() => fileRef.current.click()}
              >
                <div style={{ fontSize: '40px', marginBottom: '10px' }}>📄</div>
                <div style={S.dropText}>{loading ? 'Extracting text...' : 'Drop PDF here or click to upload'}</div>
                <div style={S.dropSub}>Trading books, course PDFs, ICT materials, strategy guides</div>
                <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
                  onChange={e => loadPDF(e.target.files[0])}/>
              </div>
            </>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ fontSize: '14px', fontWeight: '700', color: '#10b981' }}>
                  ✅ PDF Loaded: {pdfName}
                </div>
                <button style={{ padding: '5px 12px', borderRadius: '5px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '12px' }} onClick={() => { setPdfText(''); setPdfName(''); setTitle(''); }}>
                  Change PDF
                </button>
              </div>
              <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>
                {pdfText.length.toLocaleString()} characters extracted from PDF
              </div>
              <div style={{ ...S.input, height: '120px', overflow: 'auto', fontSize: '12px', color: '#9ca3af', whiteSpace: 'pre-wrap' }}>
                {pdfText.slice(0, 500)}...
              </div>
            </div>
          )}
        </div>
      )}

      {/* JSON DIRECT tab */}
      {tab === 'json' && (
        <div>
          {/* Info box */}
          <div style={{ background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', fontSize: '13px', color: '#94a3b8', lineHeight: '1.8' }}>
            <div style={{ color: THEME.colors.blue, fontWeight: '700', marginBottom: '6px' }}>📋 JSON FORMAT (तुझाच format चालेल)</div>
            <code style={{ fontSize: '11px', color: '#a5f3fc', display: 'block', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', whiteSpace: 'pre' }}>{`{
  "source": "Source Name",
  "date": "2026-06-27",
  "chunks": [
    {
      "category": "rule|concept|checklist|mistake",
      "title": "Rule Title",
      "content": "Full explanation...",
      "tags": ["tag1", "tag2"]
    }
  ]
}`}</code>
          </div>

          {/* File upload */}
          <div
            style={{ border: '2px dashed rgba(0,212,255,0.25)', borderRadius: '10px', padding: '16px', textAlign: 'center', cursor: 'pointer', marginBottom: '12px', background: 'rgba(0,212,255,0.02)' }}
            onClick={() => document.getElementById('json-file-input').click()}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = THEME.colors.blue; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)'; }}
            onDrop={e => {
              e.preventDefault();
              e.currentTarget.style.borderColor = 'rgba(0,212,255,0.25)';
              const file = e.dataTransfer.files[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = ev => { setJsonText(ev.target.result); setJsonPreview(null); setJsonSaved(false); setJsonError(''); };
              reader.readAsText(file);
            }}
          >
            <div style={{ fontSize: '28px', marginBottom: '6px' }}>🗂️</div>
            <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600' }}>Drop .json file येथे किंवा click करा</div>
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>किंवा खाली paste करा</div>
            <input id="json-file-input" type="file" accept=".json,application/json" style={{ display: 'none' }}
              onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => { setJsonText(ev.target.result); setJsonPreview(null); setJsonSaved(false); setJsonError(''); };
                reader.readAsText(file);
              }}
            />
          </div>

          {/* Paste textarea */}
          <div style={S.label}>किंवा JSON येथे PASTE करा</div>
          <textarea
            style={{ ...S.textarea, minHeight: '160px', fontFamily: 'monospace', fontSize: '12px' }}
            placeholder={'{\n  "source": "My Trading Notes",\n  "chunks": [\n    {\n      "category": "rule",\n      "title": "...",\n      "content": "...",\n      "tags": []\n    }\n  ]\n}'}
            value={jsonText}
            onChange={e => { setJsonText(e.target.value); setJsonPreview(null); setJsonSaved(false); setJsonError(''); }}
          />
          <div style={S.charCount}>{jsonText.length.toLocaleString()} characters</div>

          {jsonError && <div style={S.error}>❌ {jsonError}</div>}

          {/* Parse button */}
          {jsonText.trim() && !jsonPreview && !jsonSaved && (
            <button style={S.btn()} onClick={parseJSON}>🔍 Validate &amp; Preview</button>
          )}

          {/* Preview */}
          {jsonPreview && !jsonSaved && (
            <div style={S.resultCard}>
              <div style={S.resultTitle}>PREVIEW — IMPORT होणार</div>
              <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(0,0,0,0.3)', borderRadius: '8px' }}>
                <div style={{ fontSize: '13px', color: THEME.colors.blue, fontWeight: '700' }}>📚 {jsonPreview.source}</div>
                {jsonPreview.date && <div style={{ fontSize: '11px', color: '#64748b' }}>Date: {jsonPreview.date}</div>}
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Total chunks: <b style={{ color: '#e2e8f0' }}>{jsonPreview.chunks.length}</b>
                  &nbsp;·&nbsp;
                  Rules: <b style={{ color: '#00ff88' }}>{jsonPreview.chunks.filter(c => c.category === 'rule').length}</b>
                  &nbsp;·&nbsp;
                  Concepts: <b style={{ color: '#00d4ff' }}>{jsonPreview.chunks.filter(c => c.category === 'concept').length}</b>
                  &nbsp;·&nbsp;
                  Checklists: <b style={{ color: '#ffd700' }}>{jsonPreview.chunks.filter(c => c.category === 'checklist').length}</b>
                  &nbsp;·&nbsp;
                  Mistakes: <b style={{ color: '#f87171' }}>{jsonPreview.chunks.filter(c => c.category === 'mistake').length}</b>
                </div>
              </div>

              {/* Chunk list preview */}
              <div style={{ maxHeight: '260px', overflowY: 'auto', marginBottom: '14px' }}>
                {jsonPreview.chunks.map((c, i) => (
                  <div key={i} style={{ padding: '7px 10px', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <span style={{
                      fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '10px', flexShrink: 0, marginTop: '2px',
                      background: c.category === 'rule' ? 'rgba(0,255,136,0.12)' : c.category === 'concept' ? 'rgba(0,212,255,0.12)' : c.category === 'checklist' ? 'rgba(255,215,0,0.1)' : 'rgba(239,68,68,0.1)',
                      color:      c.category === 'rule' ? '#00ff88' : c.category === 'concept' ? '#00d4ff' : c.category === 'checklist' ? '#ffd700' : '#f87171',
                    }}>{c.category}</span>
                    <div>
                      <div style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: '600' }}>{c.title}</div>
                      <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{c.content?.slice(0, 80)}...</div>
                    </div>
                  </div>
                ))}
              </div>

              <button style={S.btn('#10b981')} onClick={importJSON}>
                ✅ JARVIS Brain मध्ये Save करा ({jsonPreview.chunks.length} chunks)
              </button>
              <button style={S.btnGhost} onClick={() => { setJsonPreview(null); }}>Back</button>
            </div>
          )}

          {/* Success */}
          {jsonSaved && jsonStats && (
            <div style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '12px', padding: '20px', marginTop: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
              <div style={{ fontSize: '16px', fontWeight: '800', color: '#10b981', marginBottom: '8px' }}>Import Complete!</div>
              <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '2' }}>
                📚 Source: <b style={{ color: '#e2e8f0' }}>{jsonStats.source}</b><br/>
                🧠 Knowledge Chunks saved: <b style={{ color: '#00d4ff' }}>{jsonStats.chunkCount}</b><br/>
                📋 Rules saved: <b style={{ color: '#00ff88' }}>{jsonStats.ruleCount}</b><br/>
                <br/>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Chat मध्ये विचारा → JARVIS आता हे knowledge वापरेल
                </span>
              </div>
              <button style={{ ...S.btnGhost, marginTop: '12px' }} onClick={() => { setJsonText(''); setJsonPreview(null); setJsonSaved(false); setJsonStats(null); setJsonError(''); }}>
                आणखी Import करा
              </button>
            </div>
          )}
        </div>
      )}

      {error && <div style={S.error}>{error}</div>}

      {/* Buttons */}
      {tab !== 'json' && (inputText.length > 100 || pdfText.length > 100) && !result && (
        <div>
          <button style={S.btn()} onClick={extract} disabled={extracting}>
            {extracting ? 'JARVIS is extracting knowledge...' : 'Extract Trading Knowledge'}
          </button>
          <button style={S.btnGhost} onClick={reset}>Clear</button>
        </div>
      )}

      {/* Extracted result */}
      {result && (
        <div style={S.resultCard}>
          <div style={S.resultTitle}>EXTRACTED KNOWLEDGE</div>
          <div style={S.resultText}>{result}</div>
          <div>
            {!saved ? (
              <button style={S.btn()} onClick={saveToKnowledge}>Save to Knowledge Base</button>
            ) : (
              <div style={S.success}>✅ Saved to Knowledge Base!</div>
            )}
            <button style={S.btnGhost} onClick={reset}>Import Another</button>
          </div>
        </div>
      )}

      {/* Usage tips */}
      {tab !== 'json' && !result && !extracting && (
        <div style={{ marginTop: '24px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', padding: '16px', fontSize: '13px', color: 'rgba(255,255,255,0.4)', lineHeight: '1.8' }}>
          <div style={{ color: THEME.colors.blue, fontWeight: '700', marginBottom: '8px' }}>WHAT YOU CAN IMPORT:</div>
          📺 <b style={{ color: '#e2e8f0' }}>YouTube Videos</b> — ICT Mentorship, Rayner Teo, Smart Money Concepts, any trading channel<br/>
          📄 <b style={{ color: '#e2e8f0' }}>PDFs</b> — Trading books, course materials, strategy guides, ICT PDF notes<br/>
          📝 <b style={{ color: '#e2e8f0' }}>Text Notes</b> — Your own handwritten notes, copy-pasted articles, webinar summaries<br/><br/>
          JARVIS extracts: Rules, Behaviors, Concepts, Entry Criteria, Risk Rules, Psychology insights
        </div>
      )}
    </div>
  );
}
